import { Component, Input } from '@angular/core';

import { translatedNoteValue } from '@services/noteTranslation/note-translation';
import { UserdataService } from '@services/userdata/userdata.service';

@Component({
  selector: 'app-note-card',
  templateUrl: './note-card.component.html',
  styleUrls: ['./note-card.component.scss'],
})
export class NoteCardComponent {
  @Input() avatarUrl: string;
  @Input() name: string;
  @Input() time: string;
  // A setter, not a plain @Input: Angular assigns inputs in template order and
  // every caller binds [type] AFTER [text], so a plain property would leave the
  // type-guard in resolveTranslation reading `undefined` and never re-run.
  @Input()
  set type(value: string) {
    this.noteType = value;
    this.resolveTranslation();
  }

  get type(): string {
    return this.noteType;
  }

  @Input() headerPostText: string[];
  @Input() headerPostTextClass: string;

  // CVX-6218. Callers that have no translation to offer (document notes,
  // review/reapproval notes, observation history) simply omit these two and
  // get exactly today's behaviour: `displayText` stays `text` and no control
  // renders. Setters rather than ngOnChanges so a direct assignment resolves
  // too, matching NoteListItemComponent.
  @Input()
  set text(value: string) {
    this.original = value;
    this.resolveTranslation();
  }

  get text(): string {
    return this.original;
  }

  @Input()
  set language(value: string) {
    this.noteLanguage = value;
    this.resolveTranslation();
  }

  @Input()
  set translations(value: any[]) {
    this.noteTranslations = value;
    this.resolveTranslation();
  }

  // Pre-computed for the template (no function calls in templates).
  public displayText = '';
  public canShowOriginal = false;
  public showingOriginal = false;

  private original: string;
  private lastOriginal: string;
  private noteType: string;
  private noteLanguage: string;
  private noteTranslations: any[];
  private translated: string = null;

  constructor(private userdataService: UserdataService) {}

  public toggleOriginal(): void {
    this.showingOriginal = !this.showingOriginal;
    this.displayText = this.showingOriginal ? this.original : this.translated;
  }

  private resolveTranslation(): void {
    // `type === 'text'` guard, mirroring NoteListItemComponent: an audio note's
    // value is an object URI and must never be substituted. The template gates
    // the toggle on type too, so this is belt-and-braces today — but the two
    // components must not drift, because only one of them is gated by a caller
    // that reliably passes `type` (the Insights Submission card passes none).
    const resolved = this.noteType === 'text'
      ? translatedNoteValue(
          { value: this.original, language: this.noteLanguage, translations: this.noteTranslations },
          this.userdataService.getLanguage())
      : this.original;

    this.translated = resolved !== this.original ? resolved : null;
    this.canShowOriginal = this.translated !== null;
    // Only drop the reader's choice for a genuinely different note — same guard
    // as NoteListItemComponent, so a list rebuild does not snap them back.
    if (this.original !== this.lastOriginal || !this.canShowOriginal) {
      this.showingOriginal = false;
    }
    this.lastOriginal = this.original;
    this.displayText = this.showingOriginal ? this.original : (this.translated ?? this.original);
  }
}
